<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('report.liability') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('report.liability'),
            active: true,
          },
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-12">
        <ts-card>
          <ts-card-wrapper>
            <form class="form-horizontal">
              <fieldset>
                <legend class="text-black">
                  {{ $t('report.criteriaFilter') }}
                </legend>
                <div class="row">
                  <div class="col-sm-12 col-lg-3 col-md-6">
                    <div class="form-group row">
                      <label
                        class="col-md-4 form-col-label control-label required"
                        >{{ $t('report.resultDate') }}</label
                      >
                      <div class="col-md-8">
                        <a-range-picker
                          class="ts-w-full"
                          valueFormat="DD-MM-YYYY"
                          format="DD-MM-YYYY"
                          v-model="model.date"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-12 col-lg-3 col-md-6">
                    <div class="form-group row">
                      <label
                        class="col-md-4 form-col-label control-label required"
                        >{{ $t('report.gameSetting') }}</label
                      >
                      <div class="col-md-8">
                        <a-select
                          v-model="model.game_setting_id"
                          class="ts-w-full"
                          :options="game_settings"
                        ></a-select>
                        <div
                          class="validate"
                          v-if="errors.has('game_setting_id')"
                        >
                          {{ errors.first('game_setting_id') }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-12 col-lg-3 col-md-6">
                    <div class="form-group row">
                      <label
                        class="col-md-4 form-col-label control-label required"
                        >{{ $t('report.gameSetName') }}</label
                      >
                      <div class="col-md-8">
                        <ts-loading-banner :loading="loadingGemset">
                          <a-select
                            v-model="model.game_set_id"
                            class="ts-w-full"
                            showSearch
                            mode="multiple"
                            optionFilterProp="children"
                            :maxTagTextLength="17"
                            :maxTagCount="1"
                            :options="gameSets"
                          ></a-select>
                        </ts-loading-banner>
                        <div class="validate" v-if="errors.has('game_set_id')">
                          {{ errors.first('game_set_id') }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-12 col-lg-3 col-md-6">
                    <div class="form-group row">
                      <div class="col-md-12">
                        <ts-button
                          color="info"
                          class="mr-2"
                          outline
                          :waiting="preview_load"
                          @click.prevent="onPreview"
                          >{{ $t('preview') }}</ts-button
                        >
                        <ts-button
                          color="success"
                          outline
                          :waiting="downloading"
                          @click.prevent="onDownload"
                        >
                          <i class="far fa-file-excel"></i>
                          {{ $t('export') }}</ts-button
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </form>
          </ts-card-wrapper>
          <div id="list" class="ts-overflow-scroll">
            <ts-table
              :columns="columns"
              :records="resources"
              :loading="preview_load"
              :rowGroup="{
                field: 'game_set_name',
              }"
            >
              <template v-slot="{ record }">
                <td class="ts-whitespace-no-wrap">
                  {{ record.game_lot_name }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.bets }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.xtimes }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.sale_amount | format_number }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.sale_amount_usd | format_number }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.winning_tickets | format_number }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.win_amount | format_number }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.win_amount_usd | format_number }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.game_profit | format_number }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.game_profit_usd | format_number }}
                </td>
                <td class="ts-whitespace-no-wrap text-right">
                  {{ record.liability | format_number }}
                </td>
              </template>
            </ts-table>
          </div>
          <ts-card-wrapper>
            <div class="d-flex justify-content-end p-15">
              <ts-pagination
                v-model="pagination"
                @navigate="onPreview"
              ></ts-pagination>
            </div>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>
<script>
import * as FileDownload from 'downloadjs'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'

export default {
  name: 'liability',
  data() {
    return {
      errors: new Errors(),
      loading: false,
      loadingGemset: false,
      preview_load: false,
      downloading: false,
      gameSets: [],
      game_settings: [],
      model: {
        game_setting_id: null,
        game_set_id: [],
        date: [moment().format('DD-MM-YYYY'), moment().format('DD-MM-YYYY')],
      },
    }
  },
  filters: {
    format_number(value) {
      return `${value.toLocaleString()}`
    },
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('report/liability', ['resources', 'pagination']),
    columns() {
      return [
        { name: this.$t('report.gameLotName') },
        { name: this.$t('report.bets'), style: 'text-align: right' },
        { name: this.$t('report.xtimes'), style: 'text-align: right' },
        { name: this.$t('report.saleAmount'), style: 'text-align: right' },
        { name: this.$t('report.saleAmountUsd'), style: 'text-align: right' },
        { name: this.$t('report.winningTickets'), style: 'text-align: right' },
        { name: this.$t('report.winAmount'), style: 'text-align: right' },
        { name: this.$t('report.winAmountUsd'), style: 'text-align: right' },
        { name: this.$t('report.gameProfit'), style: 'text-align: right' },
        { name: this.$t('report.gameProfitUsd'), style: 'text-align: right' },
        { name: this.$t('report.liability'), style: 'text-align: right' },
      ]
    },
  },
  methods: {
    ...mapActions('report/liability', [
      'getGameSetting',
      'getGameSet',
      'preview',
      'store',
    ]),
    fetchData() {
      this.loading = true
      this.game_settings = []
      this.getGameSetting()
        .then((response) => {
          this.game_settings = response.data.map((el) => ({
            value: el.game_setting_id,
            label: el.game_setting,
          }))
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async fetchGameSet() {
      this.loadingGemset = true
      this.gameSets = []
      await this.getGameSet(
        Object.assign(
          {},
          {
            game_setting_id: this.model.game_setting_id,
            date_from: this.model.date[0],
            date_to: this.model.date[1],
          }
        )
      )
        .then((response) => {
          this.gameSets = response.data.map((el) => ({
            value: el.game_set_id,
            label: el.game_set_name,
          }))
          this.model.game_set_id = []
          response.data.forEach((el) => {
            this.model.game_set_id.push(el.game_set_id)
          })
        })
        .catch((error) => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loadingGemset = false
        })
    },
    onPreview(attributes) {
      this.errors = new Errors()
      this.preview_load = true
      this.preview(
        Object.assign(
          {},
          {
            game_set_id: this.model.game_set_id,
            game_setting_id: this.model.game_setting_id,
            date_from: this.model.date[0],
            date_to: this.model.date[1],
          },
          attributes
        )
      )
        .catch((error) => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.preview_load = false
        })
    },
    onDownload() {
      this.errors = new Errors()
      this.downloading = true
      this.store({
        game_set_id: this.model.game_set_id,
        game_setting_id: this.model.game_setting_id,
        date_from: this.model.date[0],
        date_to: this.model.date[1],
      })
        .then((response) => {
          FileDownload(response, 'liability_report.xlsx')
        })
        .catch((errorData) => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(errorData))
          )
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.downloading = false
        })
    },
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('report/liability/RESET_STATE')
    next()
  },
  watch: {
    'model.game_setting_id': function (val) {
      if (val) {
        this.fetchGameSet()
      }
    },
    'model.date': function (val) {
      if (val) {
        this.fetchGameSet()
      }
    },
  },
}
</script>

<style lang="css" scoped>
#list {
  min-height: 50vh;
  max-height: 60vh;
}
</style>